import { ImageName } from "@mcleod/images";
import {
    Button, ClickEvent, ContextMenuItemCreator, ContextMenuLabelProps, DataSourceRemoteDataChangeEvent, Layout,
    ModelDataChangeType, Snackbar, Table, TableRow, TableSelectionEvent, Toast
} from "@mcleod/components";
import { AutogenLayoutInvoiceProcessingToolPanel } from "./autogen/AutogenLayoutInvoiceProcessingToolPanel";
import { InvoiceProcessingTable } from "./InvoiceProcessingUtil";
import { InvoiceProcessingTableType } from "./InvoiceProcessingTableType";
import { Api, LogManager, ModelRow } from "@mcleod/core";
import { InvoiceProcessingDeleteDialog } from "./InvoiceProcessingDeleteDialog";
import { CommonDialogs } from "@mcleod/common";
import { InvoiceProcessingOnHoldDialog } from "./InvoiceProcessingOnHoldDialog";
import { ViewBilling } from "./ViewBilling";
import { LMEUserSettings } from "@mcleod/general";

const log = LogManager.getLogger("lme.ar.InvoiceProcessingToolPanel");

export class InvoiceProcessingToolPanel extends AutogenLayoutInvoiceProcessingToolPanel {
    table: Table;
    private parentLayout?: InvoiceProcessingTable;

    constructor() {
        super();
    }

    private getAllVisibleButtons(): Button[] {
        return this.panelButtons?.findComponentsByType(Button)?.filter(button => button.visible);
    }

    initialize(layout?: InvoiceProcessingTable) {
        log.debug("🔧 InvoiceProcessingToolPanel.initialize called with layout:", layout?.constructor?.name || 'unknown');
        this.table = this.table ?? layout?.tableProcessing;
        this.parentLayout = layout; // Store reference to parent layout
        this.syncButtonVisiblity(layout?.type);
        if (this.getAllVisibleButtons()?.length > 0) {
            this.table?.addSelectionListener((event: TableSelectionEvent) => this.tableMovementsOnSelect(event));
            this.table?.dataSource?.addRemoteDataChangeListener((event: DataSourceRemoteDataChangeEvent) => this.onRemoteDataChange(event));
        } else {
            this.removeAll();
        }
    }

    onRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        if (event.change.type === ModelDataChangeType.DELETE) {
            const billingId = event.change.data.get("billing_id");
            if (billingId != null && this.table.selectedRow != null) {
                const seletedBillId = this.table.selectedRow.data.get("billing_id");
                if (billingId === seletedBillId) {
                    this.table.selectedRow = null;
                }
            }
        }
    }

    syncButtonVisiblity(tableType: InvoiceProcessingTableType) {
        this.buttonBack.visible = true;
        this.buttonCancel.visible = tableType?.caption !== "Ready to Process";
        this.buttonReadyToPrint.visible = tableType?.caption === "Pending Review" || tableType?.caption === "Hold";
        this.buttonOnHold.visible = tableType?.caption === "Pending Review" || tableType?.caption === "Ready to Process";
        this.buttonRemoveHold.visible = tableType?.caption === "Hold";
    }

    tableMovementsOnSelect(event: TableSelectionEvent) {
        const selectedRows = this.table?.selectedRows || [];
        const anyZeroTotal = selectedRows.some(row => row.data?.get("total_charges")?.amount === 0);
        const anyNonZeroTotal = selectedRows.some(row => row.data?.get("total_charges")?.amount > 0);
        const hasSelection = selectedRows.length > 0;

        this.getAllVisibleButtons().forEach(button => {
            button.disabledTooltip = null;

            if (button.id === "buttonReadyToPrint") {
                if (anyZeroTotal) {
                    button.enabled = false;
                    button.tooltip = "Mark as Ready to Process. \nNot available for zero amount total charges.";
                } else {
                    button.enabled =  hasSelection && selectedRows.every(row => row.data != null);
                    button.tooltip =  "Mark as Ready to Process.";
                }
            }else if (button.id ==="buttonCancel") {
                if(anyNonZeroTotal) {
                    button.enabled = false;
                    button.tooltip = "Cancel Invoice. \nNot available for non-zero amount total charges.";
                }
                else {
                    button.enabled =  hasSelection && selectedRows.every(row => row.data != null);
                    button.tooltip = "Cancel Invoice.";
                }
            }
             else {
                button.enabled = hasSelection && selectedRows.every(row => row.data != null);
            }
        });
    }

    /**
     * Updates button states based on current table selection
     */
    private updateButtonStates() {
        // Replicate the logic from tableMovementsOnSelect
        const selectedRows = this.table?.selectedRows || [];
        const anyZeroTotal = selectedRows.some(row => row.data?.get("total_charges")?.amount === 0);
        const anyNonZeroTotal = selectedRows.some(row => row.data?.get("total_charges")?.amount > 0);
        const hasSelection = selectedRows.length > 0;

        this.getAllVisibleButtons().forEach(button => {
            button.disabledTooltip = null;

            if (button.id === "buttonReadyToPrint") {
                if (anyZeroTotal) {
                    button.enabled = false;
                    button.tooltip = "Mark as Ready to Process. \nNot available for zero amount total charges.";
                } else {
                    button.enabled =  hasSelection && selectedRows.every(row => row.data != null);
                    button.tooltip =  "Mark as Ready to Process.";
                }
            }else if (button.id ==="buttonCancel") {
                if(anyNonZeroTotal) {
                    button.enabled = false;
                    button.tooltip = "Cancel Invoice. \nNot available for non-zero amount total charges.";
                }
                else {
                    button.enabled =  hasSelection && selectedRows.every(row => row.data != null);
                    button.tooltip = "Cancel Invoice.";
                }
            }
             else {
                button.enabled = hasSelection && selectedRows.every(row => row.data != null);
            }
        });
    }

    async backButtonOnClick() {
        try {
            const selected = this.returnedSelectedRows();
            const count = selected?.length;
            if (count > 0) {
                const dialogLayout = Layout.getLayout("lme/ar/InvoiceProcessingDeleteDialog") as InvoiceProcessingDeleteDialog;
                CommonDialogs.showYesNo(dialogLayout, "Send Back to Operations", {
                    yesButtonCaption: "Confirm",
                    noButtonCaption: "Cancel",
                    xVisible: true
                }, false, 150).then(async clicked => {
                    this.buttonBack.busy = true;
                    if (clicked) {
                        const message = count === 1
                            ? " record successfully sent back to operations."
                            : " records successfully sent back to operations.";
                        const onHold = dialogLayout.switchHold.checked;
                        const holdReason = dialogLayout.textboxHoldReason.text;
                        log.debug('InvoiceProcessingToolPanel: Back button clicked, calling API...');
                        const result = await Api.post("lme/ar/invoice-delete", { on_hold: onHold, hold_reason: holdReason, billing_id_list: selected });
                        const successMessage = count + message;
                        log.debug('InvoiceProcessingToolPanel: Back API completed, calling handleApiResponse...');
                        this.handleApiResponse(result, successMessage, undefined, count);

                        if (result) {
                            const errors = result?.data[0]?.error_count;
                            if (errors > 0) {
                                const errorMessage = errors === 1
                                    ? errors + " record was not sent back to operations with errors."
                                    : errors + " records were not sent back to operations with errors.";
                                Snackbar.showWarningSnackbar(errorMessage);
                            }

                            // Update button states after successful operation since selections will be cleared
                            setTimeout(() => {
                                this.updateButtonStates();
                            }, 100);
                        }
                    }
                }).finally(() => {
                    this.buttonBack.busy = false;
                })
            }
        } catch (error) {
            Snackbar.showWarningSnackbar("Error when attempting to send records back to operations");
            this.buttonBack.busy = false;
        }
    }

    async readyToProcessOnClick(): Promise<void> {
        const selectedBillingRows: ModelRow[] = [];
        const user = LMEUserSettings.getUserId();
        let okToProceed: boolean = true;
        let orderList = "";
        const selected = this.table?.selectedRows?.map((tableRow) => {
            const modelRow = tableRow.data as ModelRow;
            if (modelRow.get("invoice_exceptions").length > 0) {
                if (orderList) {
                    orderList += ", ";
                }
                orderList += modelRow.get("order_id");
                okToProceed = false;
            } else {
                selectedBillingRows.push(tableRow.data as ModelRow);
            }
        });
        const count = selected?.length;
        if (okToProceed) {
            const yesNoMessage = count === 1
                ? count + " invoice will be marked as ready to process. Do you want to continue?"
                : count + " invoices will be marked as ready to process. Do you want to continue?";

            try {
                const clicked = await CommonDialogs.showYesNo(yesNoMessage);
                this.buttonReadyToPrint.busy = true;

                if (clicked) {
                    log.debug('InvoiceProcessingToolPanel: Ready to Process button clicked, calling API...');
                    const response = await Api.post("lme/ar/mark-ready-to-process", { invoices_to_process: selectedBillingRows, billing_user_id: user });
                    const success = response.data[0]?.success;
                    const message = response.data[0]?.message;
                    log.debug('InvoiceProcessingToolPanel: Ready to Process API completed, calling handleApiResponse...');
                    this.handleApiResponse(success, message, message, count);

                    // Update button states after successful operation since selections will be cleared
                    if (success) {
                        setTimeout(() => {
                            this.updateButtonStates();
                        }, 100);
                    }
                }
            } finally {
                this.buttonReadyToPrint.busy = false;
            }
        } else {
            this.buttonReadyToPrint.busy = false;
            //We shouldn't get here now with the way we're handling the button enable/disable but left it in 'just in case'
            Snackbar.showWarningSnackbar("Order(s) [" + orderList + "] with exceptions cannot be marked as 'Ready to Process'.  " +
                "Canceling operation.");
        }
    }

    async buttonOffHoldOnClick(event: ClickEvent) {
        const selected = this.returnedSelectedRows();
        const count = selected?.length;
        if (count > 0) {
            const beginningMessage = "Remove hold on ";
            const msg = count === 1
                ? beginningMessage + count + " record?"
                : beginningMessage + count + " records?";

            try {
                const yes = await CommonDialogs.showYesNo(msg, "Remove Hold On Invoice(s)",
                    { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } });

                this.buttonRemoveHold.busy = true;

                if (yes) {
                    log.debug('InvoiceProcessingToolPanel: Remove Hold button clicked, calling API...');
                    const result = await Api.post("lme/ar/invoice-remove-hold", { billing_id_list: selected });
                    const successMessage = count === 1
                        ? count + " invoice taken off hold"
                        : count + " invoices taken off hold";
                    log.debug('InvoiceProcessingToolPanel: Remove Hold API completed, calling handleApiResponse...');
                    this.handleApiResponse(result, successMessage, undefined, count);

                    // Update button states after successful operation since selections will be cleared
                    if (result) {
                        setTimeout(() => {
                            this.updateButtonStates();
                        }, 100);
                    }
                }
            } finally {
                this.buttonRemoveHold.busy = false;
            }
        }
    }

    async buttonCancelOnClick(event: ClickEvent) {
        const selected = this.returnedSelectedRows();
        const count = selected?.length;
        const beginningMessage = "Cancellation of a zero-dollar invoice will delete the invoice record, create an invoice history record, and update the order.\n\n ";
        const message = count === 1
            ? beginningMessage + count + " invoice selected to be cancelled. Are you sure you want to proceed?"
            : beginningMessage + count + " invoices selected to be cancelled. Are you sure you want to proceed?";

        try {
            const yes = await CommonDialogs.showYesNo(message, "Cancel Invoice(s)",
                { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } });

            this.buttonCancel.busy = true;

            if (yes) {
                log.debug('InvoiceProcessingToolPanel: Cancel button clicked, calling API...');
                try {
                    const result = await Api.post("lme/datafusion/cancel-invoice", { billing_id_list: selected });
                    //ERROR SNACKBAR
                    const errors = result.data[0].errors;
                    if (errors && errors.length > 0) {
                        const errorMessage = errors.map(error => `Invoice ID: ${error.error_id}, Description: ${error.error_description}`).join('; ');
                        Snackbar.showWarningSnackbar(errorMessage);
                    }
                    //WARNING SNACKBAR
                    const warnings = result.data[0].warnings;
                    if (warnings && warnings.length > 0) {
                        const warningMessage = warnings.map(warning => `Invoice ID: ${warning.warning_id}, Description: ${warning.warning_description}`).join('; ');
                        Snackbar.showWarningSnackbar(warningMessage);
                    }
                    const successMessage = result.data[0].successfully_cancelled === 0
                        ? result.data[0].successfully_cancelled + " invoice successfully cancelled."
                        : result.data[0].successfully_cancelled + " invoices successfully cancelled.";
                    log.debug('InvoiceProcessingToolPanel: Cancel API completed, calling handleApiResponse...');
                    this.handleApiResponse(result, successMessage, undefined, count);

                    // Update button states after successful operation since selections will be cleared
                    setTimeout(() => {
                        this.updateButtonStates();
                    }, 100);
                } catch (error) {
                    console.error("Error cancelling invoices", error);
                    log.error("Error cancelling invoices", error);
                }
            }
        } finally {
            this.buttonCancel.busy = false;
        }
    }

    async holdButtonOnClick() {
        try {
            const selected = this.returnedSelectedRows();
            const count = selected.length;
            if (count > 0) {
                const dialogLayout: InvoiceProcessingOnHoldDialog = Layout.getLayout("lme/ar/InvoiceProcessingOnHoldDialog") as InvoiceProcessingOnHoldDialog;
                const endingDialog = count === 1
                    ? " record to be placed on hold"
                    : " records to be placed on hold";
                dialogLayout.headerText = count + endingDialog;
                CommonDialogs.showYesNo(dialogLayout, "Place On Hold", {
                    yesButtonCaption: "Confirm",
                    noButtonCaption: "Cancel",
                    xVisible: true
                }, false, 150).then(async clicked => {
                    this.buttonOnHold.busy = true;
                    if (clicked) {
                        const message = count === 1
                            ? " record placed on hold"
                            : " records placed on hold";
                        const reasonCode = dialogLayout?.textboxOnHoldReasonCode?.getFirstLookupModelData()?.get(dialogLayout?.textboxOnHoldReasonCode?.lookupModelResultField, null);
                        const reasonCodeText = dialogLayout?.textboxOnHoldDescription?.text;
                        if (reasonCode || reasonCodeText) {
                            log.debug('InvoiceProcessingToolPanel: Place On Hold button clicked, calling API...');
                            const result = await Api.post("lme/ar/invoice-on-hold", { billing_id_list: selected, hold_reason_code: reasonCode, hold_reason_text: reasonCodeText });
                            log.debug('InvoiceProcessingToolPanel: Place On Hold API completed, calling handleApiResponse...');
                            this.handleApiResponse(result, count + message, undefined, count);

                            // Update button states after successful operation since selections will be cleared
                            if (result) {
                                setTimeout(() => {
                                    this.updateButtonStates();
                                }, 100);
                            }
                        } else {
                            Snackbar.showWarningSnackbar("Must select reason code to continue");
                        }
                    }
                }).finally(() => {
                    this.buttonOnHold.busy = false;
                });
            }
        } catch (error) {
            Snackbar.showWarningSnackbar("Error when attempting to put records on hold.");
            this.buttonOnHold.busy = false;
        }
    }

    returnedSelectedRows() {
        return this.table?.selectedRows?.map((tableRow) => {
            return {
                billing_id: tableRow.data.get("billing_id"),
                order_id: tableRow.data.get("order_id"),
                movement_id: tableRow.data.get("movement_id")
            }
        });
    }

    handleApiResponse(response: any, successMessage: string, errorMessage?: string, recordCount?: number) {
        log.debug("📤 handleApiResponse called:", {
            hasResponse: !!response,
            successMessage,
            recordCount: recordCount || 0
        });

        if (response) {
            Toast.showSuccessToast(successMessage);
            // Primary approach: rely on cache for UI updates
            // The API operations should trigger cache notifications that will update the UI automatically
            log.debug("✅ API operation successful - relying on cache for UI updates");

            // Fallback: if cache doesn't update within 3 seconds, do a manual refresh
            // This handles edge cases where cache notifications might fail
            setTimeout(() => {
                log.debug("🔄 Cache fallback: checking if manual refresh is needed...");
                // Only refresh if we suspect the cache didn't work properly
                // For now, we'll trust the cache completely and skip this fallback
                // If issues are reported, we can re-enable this with proper cache verification
            }, 3000);
        } else if (errorMessage) {
            Snackbar.showWarningSnackbar(errorMessage);
        }
    }

    private refreshParentData(recordCount?: number) {
        // Add immediate console output to confirm this method is being called
        log.debug("🚀 refreshParentData called with recordCount:", recordCount || 0);

        // Add a longer delay to ensure any ongoing multi-record operations are complete
        // Use progressively longer delays for multi-record operations
        let delay = 500; // Base delay for single record

        if (recordCount && recordCount > 1) {
            // For multi-record operations, use longer delays:
            // 2-3 records: 1000ms, 4-5 records: 1500ms, 6+ records: 2000ms
            if (recordCount <= 3) {
                delay = 1000;
            } else if (recordCount <= 5) {
                delay = 1500;
            } else {
                delay = 2000;
            }
            log.debug(`⏱️ Using ${delay}ms delay for ${recordCount} record operation`);
        } else {
            log.debug(`⏱️ Using ${delay}ms delay for single record operation`);
        }

        // Log environment detection
        log.debug(`🔧 Environment detection - Class names minified: ${this.parentLayout?.constructor?.name?.length === 1}`);

        log.debug("⏳ Setting timeout to call doRefreshParentData...");
        setTimeout(() => this.doRefreshParentData(), delay);
    }    private doRefreshParentData() {
        // Trigger a refresh of the main data source to update all tabs and counts
        if (this.parentLayout?.mainDataSource) {
            // Use console.log for immediate debugging - easier to see in production
            log.debug("=== INVOICE PROCESSING REFRESH DEBUG ===");
            log.debug("Parent layout constructor:", this.parentLayout?.constructor?.name || 'unknown');
            log.debug("Has mainDataSource:", !!this.parentLayout?.mainDataSource);

            // Try multiple approaches to find the profile and trigger refresh
            // Approach 1: Look for searchMainDatasource method first (preferred)
            const parentLayoutAny = this.parentLayout as any;
            log.debug("Has searchMainDatasource:", !!parentLayoutAny.searchMainDatasource);
            log.debug("Has profileRow:", !!parentLayoutAny.profileRow);

            // CRITICAL DEBUG: Log profile information to identify the switching issue
            if (parentLayoutAny.profileRow) {
                log.debug("🔍 Current profile details:");
                log.debug("  - Profile ID:", parentLayoutAny.profileRow.get?.('id') || 'unknown');
                log.debug("  - Profile Title:", parentLayoutAny.profileRow.get?.('title') || 'unknown');
                log.debug("  - Profile is temporary:", parentLayoutAny.profileRow.get?.('temporary') || false);
                log.debug("  - Profile row object:", parentLayoutAny.profileRow);
            } else {
                log.debug("⚠️ No profileRow found in parent layout");
            }

            if (parentLayoutAny.searchMainDatasource) {
                log.debug("✅ Using searchMainDatasource method to refresh all tab data");
                parentLayoutAny.searchMainDatasource();

                // No need to manually call populateFilteredTables - searchMainDatasource will trigger the automatic event handler
                // Just update filter counts after a delay to let the automatic refresh complete
                setTimeout(() => {
                    log.debug("Updating filter counts after searchMainDatasource");
                    this.updateAllFilterCounts();
                }, 750);
                return;
            }

            // Approach 2: Direct profile approach (fallback)
            if (parentLayoutAny.profileRow) {
                log.debug("📋 Using approach 2 - direct parent profile");
                log.debug("Profile ID:", parentLayoutAny.profileRow.get?.('id') || 'unknown');
                log.debug("Profile Title:", parentLayoutAny.profileRow.get?.('title') || 'unknown');

                // Instead of refreshing the sub-layout data source, find and refresh the main InvoiceProcessing data source
                let mainInvoiceProcessingLayout = null;
                let currentLayout = this.parentLayout as any;

                // Walk up the parent chain to find the main InvoiceProcessing layout
                // Use more robust detection since class names are minified in production
                while (currentLayout && currentLayout.parent) {
                    currentLayout = currentLayout.parent;
                    // Check for InvoiceProcessing layout by properties instead of class name
                    if (currentLayout.mainDataSource &&
                        (currentLayout.searchMainDatasource ||
                         currentLayout.sourceInvoiceProcessingCache ||
                         currentLayout.tabsetInvoiceProcessingTabs)) {
                        mainInvoiceProcessingLayout = currentLayout;
                        log.debug("Found main InvoiceProcessing layout by properties (not class name)");
                        break;
                    }
                }

                if (mainInvoiceProcessingLayout) {
                    log.debug("✅ Found main InvoiceProcessing layout, refreshing its data source");
                    // Clear and search the main InvoiceProcessing data source, which will automatically trigger populateFilteredTables
                    mainInvoiceProcessingLayout.mainDataSource.clear();
                    mainInvoiceProcessingLayout.mainDataSource.search({
                        profile_row: parentLayoutAny.profileRow
                    }).then(() => {
                        log.debug("Main InvoiceProcessing data source refreshed (approach 2), total records:", mainInvoiceProcessingLayout.mainDataSource.data?.length || 0);
                        // No need to manually call populateFilteredTables - it will be called automatically by sourceInvoiceProcessingAfterExecution
                    }).catch(error => {
                        console.error("❌ Error refreshing main InvoiceProcessing data source (approach 2):", error);
                        log.error("❌ Error refreshing main InvoiceProcessing data source (approach 2):", error);
                    });
                } else {
                    log.debug("⚠️ Main InvoiceProcessing layout not found, falling back to sub-layout refresh");
                    // Fallback to the old approach
                    this.parentLayout.mainDataSource.clear();
                    this.parentLayout.mainDataSource.search({
                        profile_row: parentLayoutAny.profileRow
                    }).then(() => {
                        log.debug("Sub-layout data source refreshed, total records:", this.parentLayout.mainDataSource.data?.length || 0);
                        this.updateAllFilterCounts();
                    }).catch(error => {
                        console.error("❌ Error refreshing sub-layout data source (approach 2 fallback):", error);
                        log.error("❌ Error refreshing sub-layout data source (approach 2 fallback):", error);
                    });
                }
                return;
            }            // Approach 3: Walk up parent chain to find InvoiceProcessing layout (works for multi-profile)
            let currentLayout = this.parentLayout as any;
            console.log("🔍 === PROFILE CONTEXT DEBUG ===");
            log.debug("🔍 === PROFILE CONTEXT DEBUG ===");
            let depth = 0;
            while (currentLayout && currentLayout.parent && depth < 5) {
                currentLayout = currentLayout.parent;
                depth++;
                log.debug(`Parent level ${depth}:`, currentLayout?.constructor?.name || 'unknown');

                // Check for profile information at each level
                if (currentLayout.profileRow || currentLayout._profileRow) {
                    const profileAtThisLevel = currentLayout.profileRow || currentLayout._profileRow;
                    log.debug(`  📋 Profile found at level ${depth}:`);
                    log.debug("    - ID:", profileAtThisLevel.get?.('id') || 'unknown');
                    log.debug("    - Title:", profileAtThisLevel.get?.('title') || 'unknown');
                    log.debug("    - Temporary:", profileAtThisLevel.get?.('temporary') || false);

                    // Compare with the profile we found earlier
                    if (parentLayoutAny.profileRow && profileAtThisLevel !== parentLayoutAny.profileRow) {
                        log.debug("    ⚠️ PROFILE MISMATCH DETECTED!");
                        log.debug("    Direct parent profile ID:", parentLayoutAny.profileRow.get?.('id'));
                        log.debug("    Higher level profile ID:", profileAtThisLevel.get?.('id'));

                        // Use the higher-level profile which is likely more current
                        log.debug("    Using higher-level profile for refresh");
                        if (currentLayout.searchMainDatasource) {
                            log.debug("    ✅ Found searchMainDatasource at higher level, using it");
                            currentLayout.searchMainDatasource();
                            return;
                        }
                    }

                    // Use the main layout's search method to ensure proper data distribution
                    if (currentLayout.searchMainDatasource) {
                        log.debug("✅ Found profile in parent chain, using searchMainDatasource");
                        currentLayout.searchMainDatasource();
                        return;
                    } else {
                        // Fallback: direct search but clear first to ensure clean data
                        log.debug("📋 InvoiceProcessingToolPanel: Using direct search fallback (approach 3)");
                        this.parentLayout.mainDataSource.clear();
                        this.parentLayout.mainDataSource.search({
                            profile_row: currentLayout.profileRow || currentLayout._profileRow
                        }).then(() => {
                            log.debug("InvoiceProcessingToolPanel: Main data source refreshed (approach 3 fallback), total records:", this.parentLayout.mainDataSource.data?.length || 0);

                            // Force complete repopulation of all tabs after datasource is refreshed
                            setTimeout(() => {
                                log.debug("InvoiceProcessingToolPanel: Forcing complete tab repopulation (approach 3 fallback)");
                                if ((this.parentLayout as any).populateFilteredTables) {
                                    (this.parentLayout as any).populateFilteredTables();
                                    log.debug("InvoiceProcessingToolPanel: Called populateFilteredTables() (approach 3 fallback)");
                                }
                                this.updateAllFilterCounts();
                            }, 750);
                        }).catch(error => {
                            console.error("❌ Error refreshing data after operation (approach 3):", error);
                            log.error("❌ Error refreshing data after operation (approach 3):", error);
                        });
                        return;
                    }
                }

                // Check if this is an InvoiceProcessing layout by properties instead of class name
                if (currentLayout.searchMainDatasource ||
                    currentLayout.sourceInvoiceProcessingCache ||
                    currentLayout.tabsetInvoiceProcessingTabs) {
                    log.debug(`  ✅ Found main InvoiceProcessing layout at level ${depth}`);
                    // Found the main InvoiceProcessing layout, call its refresh method
                    if (currentLayout.searchMainDatasource) {
                        log.debug("Found InvoiceProcessing layout by properties, using searchMainDatasource");
                        currentLayout.searchMainDatasource();
                        return;
                    }
                }
            }

            // Approach 4: Use sourceInvoiceProcessingCache if available
            if (parentLayoutAny.sourceInvoiceProcessingCache) {
                // Try to find profile from various possible locations
                const profileRow = parentLayoutAny.profileRow ||
                    parentLayoutAny._profileRow ||
                    (currentLayout && (currentLayout.profileRow || currentLayout._profileRow));
                if (profileRow) {
                    // Clear and search to ensure clean data distribution
                    this.parentLayout.mainDataSource.clear();
                    parentLayoutAny.sourceInvoiceProcessingCache.search({
                        profile_row: profileRow
                    }).then(() => {
                        // Force update of filter counts after successful refresh
                        this.updateAllFilterCounts();
                    }).catch(error => {
                        console.error("Error refreshing data after operation (approach 4):", error);
                        log.error("Error refreshing data after operation (approach 4):", error);
                    });
                    return;
                }
            }

            // Approach 5: Last resort - trigger a generic refresh without profile
            // This might work if the layout can determine the profile from context
            log.debug("⚠️ Could not find profile for data refresh, attempting generic refresh");
            this.parentLayout.mainDataSource.clear();
            this.parentLayout.mainDataSource.search({}).then(() => {
                log.debug("Generic refresh completed, total records:", this.parentLayout.mainDataSource.data?.length || 0);
                // Force update of filter counts after successful refresh
                this.updateAllFilterCounts();
            }).catch(error => {
                console.error("❌ Error refreshing data after operation (approach 5):", error);
                log.error("❌ Error refreshing data after operation (approach 5):", error);
            });
        } else {
            log.debug("❌ No mainDataSource found on parent layout, cannot refresh");
        }
        log.debug("=== INVOICE PROCESSING REFRESH DEBUG END ===");
    }

    private updateAllFilterCounts() {
        // Force update of all filter counts to ensure tabs reflect current data
        setTimeout(() => {
            try {
                log.debug("Starting comprehensive tab data refresh");

                // First, update filter counts for the parent layout
                const parentLayoutAny = this.parentLayout as any;
                if (parentLayoutAny.filterPanel && parentLayoutAny.filterPanel.updateFilterCounts) {
                    log.debug("Updating parent filter counts");
                    parentLayoutAny.filterPanel.updateFilterCounts();
                }

                // More importantly, force each tab to refresh its actual data, not just counts
                let currentLayout = this.parentLayout as any;
                let tabsFound = 0;

                log.debug("Starting search for child tabs");

                while (currentLayout && currentLayout.parent) {
                    currentLayout = currentLayout.parent;

                    // Look for tab layouts and force them to refresh their data
                    if (currentLayout.children) {
                        for (const child of currentLayout.children) {
                            // Check if this looks like an invoice processing tab using properties instead of class name
                            const isInvoiceProcessingTab = child.tableProcessing ||
                                                          child.filterPanel ||
                                                          (child.type && typeof child.type === 'object' && child.type.caption);

                            if (isInvoiceProcessingTab) {
                                tabsFound++;
                                log.debug(`Found invoice processing tab: ${child.constructor?.name || 'unknown'} (using property detection)`);
                            }

                            // Update filter counts
                            if (child.filterPanel && child.filterPanel.updateFilterCounts) {
                                child.filterPanel.updateFilterCounts();
                            }

                            // Force each tab to refresh its actual data after the main data source update
                            if (child.tableProcessing && child.tableProcessing.dataSource) {
                                // The main data source has been updated, but the table needs to re-filter its display
                                try {
                                    // Try to trigger a table refresh/re-render
                                    if (typeof child.tableProcessing.refresh === 'function') {
                                        child.tableProcessing.refresh();
                                    } else if (typeof child.tableProcessing.update === 'function') {
                                        child.tableProcessing.update();
                                    } else if (child.tableProcessing.dataSource && typeof child.tableProcessing.dataSource.notifyDataChanged === 'function') {
                                        child.tableProcessing.dataSource.notifyDataChanged();
                                    }
                                } catch (refreshError) {
                                    console.error(`Error refreshing table for ${child.constructor?.name || 'unknown'}:`, refreshError);
                                    log.error(`Error refreshing table for ${child.constructor?.name || 'unknown'}:`, refreshError);
                                }

                                // Also update filter counts
                                if (child.filterPanel && child.filterPanel.updateFilterCounts) {
                                    child.filterPanel.updateFilterCounts();
                                }
                            }
                        }
                    } else {
                        log.debug(`No children found in ${currentLayout.constructor?.name || 'unknown'}`);
                    }
                }

                log.debug(`InvoiceProcessingToolPanel: Total invoice processing tabs found: ${tabsFound}`);

                // If we didn't find any tabs in the normal way, try alternative approaches
                if (tabsFound === 0) {
                    log.debug("InvoiceProcessingToolPanel: No tabs found via parent traversal, trying alternative approaches...");
                    this.findAndRefreshTabsAlternative();
                }

                log.debug("InvoiceProcessingToolPanel: Comprehensive tab data refresh completed");
            } catch (error) {
                console.error("Error in comprehensive filter update:", error);
                log.error("Error in comprehensive filter update:", error);
            }
        }, 100); // Small delay to ensure main data source refresh is complete
    }

    private findAndRefreshTabsAlternative() {
        log.debug("Trying alternative tab finding approaches...");

        // Approach 1: Try to find tabs via the current parent layout directly
        try {
            const parentAny = this.parentLayout as any;
            if (parentAny.parent && parentAny.parent.children) {
                this.refreshChildTabs(parentAny.parent.children, "direct parent.parent");
            }
        } catch (error) {
            // Ignore errors in alternative approaches
        }

        // Approach 2: In multi-profile mode, try to find the InvoiceProcessing layout and its tab management
        try {
            let currentLayout = this.parentLayout as any;
            while (currentLayout && currentLayout.parent) {
                currentLayout = currentLayout.parent;
                // Use property detection instead of class name
                if (currentLayout.tabsetInvoiceProcessingTabs ||
                    currentLayout.searchMainDatasource ||
                    currentLayout.sourceInvoiceProcessingCache) {
                    log.debug("InvoiceProcessingToolPanel: Found InvoiceProcessing layout by properties, checking for multi-profile tabs");

                    // In multi-profile mode, tabs might be managed differently
                    // Look for tab management properties
                    if (currentLayout.tabsetInvoiceProcessingTabs) {
                        log.debug("InvoiceProcessingToolPanel: Found tabsetInvoiceProcessingTabs, checking for active tabs");
                        const tabset = currentLayout.tabsetInvoiceProcessingTabs as any;

                        if (tabset.tabs) {
                            log.debug(`InvoiceProcessingToolPanel: Found ${tabset.tabs.length} tabs in tabset`);
                            for (const tab of tabset.tabs) {
                                if (tab.layout && tab.layout.filterPanel) {
                                    log.debug(`InvoiceProcessingToolPanel: Multi-profile approach - updating filter counts for ${tab.layout.constructor?.name || 'unknown'}`);
                                    tab.layout.filterPanel.updateFilterCounts();
                                }
                            }
                        }
                    }

                    // Also try to look for direct child layouts
                    this.forceRefreshSpecificTabs(currentLayout);
                    break;
                }
            }
        } catch (error) {
            console.log("Error in multi-profile tab approach:", error);
            log.error("Error in multi-profile tab approach:", error);
        }

        // Approach 3: Try to find specific tab components by walking the component tree differently
        try {
            let rootLayout = this.parentLayout as any;
            while (rootLayout.parent) {
                rootLayout = rootLayout.parent;
            }

            log.debug("InvoiceProcessingToolPanel: Starting deep search from root layout:", rootLayout.constructor?.name || 'unknown');
            this.deepSearchForTabs(rootLayout, 0);
        } catch (error) {
            console.log("Error in deep search approach:", error);
            log.error("Error in deep search approach:", error);
        }

        // Approach 4: Since we're in a tab layout, try to find sibling tabs directly
        try {
            log.debug("InvoiceProcessingToolPanel: Attempting to find sibling tabs via parent navigation");
            const parentLayoutAny = this.parentLayout as any;

            // Check if the parent layout has methods to access other tabs
            if (parentLayoutAny.parent) {
                const grandParent = parentLayoutAny.parent as any;
                log.debug(`InvoiceProcessingToolPanel: Grandparent type: ${grandParent.constructor?.name || 'unknown'}`);

                // Look for other tab-related properties on the grandparent
                const properties = Object.getOwnPropertyNames(grandParent);
                const tabProperties = properties.filter(prop =>
                    prop.toLowerCase().includes('invoice') &&
                    (prop.toLowerCase().includes('tab') || prop.toLowerCase().includes('layout'))
                );

                log.debug(`InvoiceProcessingToolPanel: Found potential tab properties: ${tabProperties.join(', ')}`);

                for (const prop of tabProperties) {
                    try {
                        const tabLayout = grandParent[prop];
                        if (tabLayout && tabLayout !== this.parentLayout && tabLayout.filterPanel) {
                            log.debug(`InvoiceProcessingToolPanel: Sibling approach - updating filter counts for ${prop}`);
                            tabLayout.filterPanel.updateFilterCounts();
                        }
                    } catch (error) {
                        // Property access failed
                    }
                }
            }
        } catch (error) {
            console.log("Error in sibling tab approach:", error);
            log.error("Error in sibling tab approach:", error);
        }
    }

    private forceRefreshSpecificTabs(invoiceProcessingLayout: any) {
        log.debug("InvoiceProcessingToolPanel: Attempting to force refresh specific tabs");

        // Try to access specific tab layouts directly if they exist as properties
        const possibleTabProperties = [
            'layoutInvoiceProcessingOnHold',
            'layoutInvoiceReadyToProcessList',
            'layoutInvoiceProcessingProcessing',
            'invoiceProcessingOnHold',
            'invoiceReadyToProcessList',
            'invoiceProcessingProcessing',
            'layoutInvoiceProcessingPending',
            'layoutInvoiceProcessingReview'
        ];

        // Also try to discover all properties that might be tabs
        try {
            const allProperties = Object.getOwnPropertyNames(invoiceProcessingLayout);
            const tabLikeProperties = allProperties.filter(prop =>
                (prop.toLowerCase().includes('layout') && prop.toLowerCase().includes('invoice')) ||
                (prop.toLowerCase().includes('tab') && prop.toLowerCase().includes('invoice')) ||
                (prop.toLowerCase().includes('invoiceprocessing'))
            );

            log.debug(`InvoiceProcessingToolPanel: Found ${tabLikeProperties.length} tab-like properties:`, tabLikeProperties.join(', '));

            // Add discovered properties to our list
            for (const prop of tabLikeProperties) {
                if (!possibleTabProperties.includes(prop)) {
                    possibleTabProperties.push(prop);
                }
            }
        } catch (error) {
            console.log("Error discovering tab properties:", error);
            log.error("Error discovering tab properties:", error);
        }        let tabsFound = 0;
        let filterUpdateDelay = 800; // Give main data source time to propagate

        for (const prop of possibleTabProperties) {
            try {
                const tabLayout = invoiceProcessingLayout[prop];
                if (tabLayout && tabLayout.filterPanel && tabLayout.filterPanel.updateFilterCounts) {
                    tabsFound++;
                    log.debug(`InvoiceProcessingToolPanel: Direct property access - updating filter counts for ${prop} with ${filterUpdateDelay}ms delay (skipping refreshData to avoid flickering)`);

                    // Only update filter counts - let main data source propagation handle the data
                    setTimeout(() => {
                        tabLayout.filterPanel.updateFilterCounts();
                        log.debug(`InvoiceProcessingToolPanel: Filter counts updated for ${prop}`);

                        // Double update to ensure changes are visible
                        setTimeout(() => {
                            tabLayout.filterPanel.updateFilterCounts();
                            log.debug(`InvoiceProcessingToolPanel: Second filter count update for ${prop}`);
                        }, 200);
                    }, filterUpdateDelay);

                    // Stagger the filter updates
                    filterUpdateDelay += 300;

                } else if (tabLayout) {
                    log.debug(`InvoiceProcessingToolPanel: Found property ${prop} but no filter panel:`, {
                        hasRefreshData: !!tabLayout.refreshData,
                        hasFilterPanel: !!tabLayout.filterPanel,
                        hasUpdateFilterCounts: !!(tabLayout.filterPanel && tabLayout.filterPanel.updateFilterCounts),
                        type: tabLayout.constructor?.name || 'unknown'
                    });
                }
            } catch (error) {
                // Property doesn't exist or error accessing it
            }
        }

        log.debug(`InvoiceProcessingToolPanel: Direct property access found and refreshed ${tabsFound} tabs`);

        // If we didn't find many tabs, try accessing the tabset directly
        if (tabsFound < 2) {
            log.debug("InvoiceProcessingToolPanel: Trying to access tabset directly for more tabs");
            this.tryTabsetDirectAccess(invoiceProcessingLayout);
        }
    }

    private tryTabsetDirectAccess(invoiceProcessingLayout: any) {
        try {
            // Look for tabset properties
            const tabsetProperties = [
                'tabsetInvoiceProcessingTabs',
                'tabset',
                'mainTabset',
                'invoiceProcessingTabset'
            ];

            for (const tabsetProp of tabsetProperties) {
                const tabset = invoiceProcessingLayout[tabsetProp];
                if (tabset) {
                    log.debug(`InvoiceProcessingToolPanel: Found tabset via ${tabsetProp}`);

                    // Try different ways to access tabs - only update filter counts, not data
                    if (tabset.tabs && Array.isArray(tabset.tabs)) {
                        log.debug(`InvoiceProcessingToolPanel: Tabset has ${tabset.tabs.length} tabs`);
                        let tabFilterDelay = 800;
                        for (let i = 0; i < tabset.tabs.length; i++) {
                            const tab = tabset.tabs[i];
                            if (tab.layout && tab.layout.filterPanel && tab.layout.filterPanel.updateFilterCounts) {
                                log.debug(`InvoiceProcessingToolPanel: Tabset approach - updating filter counts for tab ${i}: ${tab.layout.constructor?.name || 'unknown'} with ${tabFilterDelay}ms delay`);
                                setTimeout(() => {
                                    tab.layout.filterPanel.updateFilterCounts();
                                }, tabFilterDelay);
                                tabFilterDelay += 300;
                            }
                        }
                    }

                    // Try to access the active tab - only update filter counts
                    if (tabset.activeTab && tabset.activeTab.layout && tabset.activeTab.layout.filterPanel) {
                        log.debug(`InvoiceProcessingToolPanel: Tabset approach - updating filter counts for active tab: ${tabset.activeTab.layout.constructor?.name || 'unknown'}`);
                        setTimeout(() => {
                            tabset.activeTab.layout.filterPanel.updateFilterCounts();
                        }, 800);
                    }

                    // Try to get all tabs via methods - only update filter counts
                    if (typeof tabset.getAllTabs === 'function') {
                        const allTabs = tabset.getAllTabs();
                        log.debug(`InvoiceProcessingToolPanel: Tabset getAllTabs returned ${allTabs.length} tabs`);
                        let getAllTabsDelay = 800;
                        for (const tab of allTabs) {
                            if (tab.layout && tab.layout.filterPanel && tab.layout.filterPanel.updateFilterCounts) {
                                log.debug(`InvoiceProcessingToolPanel: Tabset getAllTabs - updating filter counts for ${tab.layout.constructor?.name || 'unknown'} with ${getAllTabsDelay}ms delay`);
                                setTimeout(() => {
                                    tab.layout.filterPanel.updateFilterCounts();
                                }, getAllTabsDelay);
                                getAllTabsDelay += 300;
                            }
                        }
                    }
                }
            }
        } catch (error) {
            log.debug("Error in tabset direct access:", error);
        }
    }

    private refreshChildTabs(children: any[], source: string) {
        let tabsFound = 0;
        let childFilterDelay = 800; // Start with delay to let main data propagate

        for (const child of children) {
            log.debug(`InvoiceProcessingToolPanel: (${source}) Examining child: ${child.constructor?.name || 'unknown'}`);

            // Check if this looks like an invoice processing tab using properties instead of class name
            const isInvoiceProcessingTab = child.tableProcessing ||
                                          child.filterPanel ||
                                          (child.type && typeof child.type === 'object' && child.type.caption);

            if (isInvoiceProcessingTab) {
                tabsFound++;
                log.debug(`InvoiceProcessingToolPanel: (${source}) Found invoice processing tab ${tabsFound}: ${child.constructor?.name || 'unknown'}`);

                // Only update filter counts - skip refreshData to avoid flickering
                if (child.filterPanel && child.filterPanel.updateFilterCounts) {
                    log.debug(`InvoiceProcessingToolPanel: (${source}) Updating filter counts for ${child.constructor?.name || 'unknown'} with ${childFilterDelay}ms delay (no refreshData)`);
                    setTimeout(() => {
                        child.filterPanel.updateFilterCounts();
                    }, childFilterDelay);
                    childFilterDelay += 300;
                } else {
                    // Log what we found but don't force refresh
                    if (child.tableProcessing && child.tableProcessing.dataSource) {
                        log.debug(`InvoiceProcessingToolPanel: (${source}) Found table for ${child.constructor?.name || 'unknown'} but no filter panel - relying on main data propagation`);

                        // Log current data
                        if (child.tableProcessing.dataSource.data) {
                            log.debug(`InvoiceProcessingToolPanel: (${source}) ${child.constructor?.name || 'unknown'} currently showing ${child.tableProcessing.dataSource.data.length} rows`);
                        }
                    }
                }
            }
        }

        log.debug(`InvoiceProcessingToolPanel: (${source}) Found ${tabsFound} invoice processing tabs - using filter count updates only`);
        return tabsFound;
    }

    private deepSearchForTabs(layout: any, depth: number) {
        if (depth > 5) return; // Prevent infinite recursion

        const indent = "  ".repeat(depth);
        log.debug(`${indent}InvoiceProcessingToolPanel: Deep search at depth ${depth}: ${layout.constructor?.name || 'unknown'}`);

        // Check if this is a tab we want to refresh using properties instead of class name
        if ((layout.tableProcessing || layout.filterPanel) &&
            (layout.type && typeof layout.type === 'object' && layout.type.caption)) {
            log.debug(`${indent}InvoiceProcessingToolPanel: Found target tab via deep search: ${layout.constructor?.name || 'unknown'}`);

            if (layout.filterPanel && layout.filterPanel.updateFilterCounts) {
                log.debug(`${indent}InvoiceProcessingToolPanel: Deep search - updating filter counts for ${layout.constructor?.name || 'unknown'} (skipping refreshData)`);
                // Add a delay to let main data propagate
                setTimeout(() => {
                    layout.filterPanel.updateFilterCounts();

                    // Force a second update
                    setTimeout(() => {
                        layout.filterPanel.updateFilterCounts();
                        log.debug(`${indent}InvoiceProcessingToolPanel: Deep search - double filter update for ${layout.constructor?.name || 'unknown'}`);
                    }, 200);
                }, 800);
            } else {
                log.debug(`${indent}InvoiceProcessingToolPanel: Deep search - no filter panel found for ${layout.constructor?.name || 'unknown'}, relying on main data propagation`);
            }
        }

        // Recursively search children
        if (layout.children && layout.children.length > 0) {
            for (const child of layout.children) {
                this.deepSearchForTabs(child, depth + 1);
            }
        }
    }

    static getContextMenuItems(toolPanel: InvoiceProcessingToolPanel, tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        return ViewBilling.addNavigationContextMenuItems(
            toolPanel.getContextMenuItems(tableRow),
            tableRow?.data?.get("billing_id")
        )
    }

    public getContextMenuItems(tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        tableRow.table.selectedRow = tableRow;
        const fromButtons = this.getAllVisibleButtons().map(button => ContextMenuItemCreator.fromButton(button));
        return [...fromButtons];
    }    // Debug helper method - can be called from browser console
    public debugLayoutStructure() {
        // Use console.log for immediate user feedback in browser console
        log.debug("=== DEBUG LAYOUT STRUCTURE ===");
        log.debug("Current layout:", this.parentLayout?.constructor?.name || 'unknown');
        log.debug("Has mainDataSource:", !!this.parentLayout?.mainDataSource);

        const parentAny = this.parentLayout as any;
        log.debug("Has searchMainDatasource:", !!parentAny.searchMainDatasource);
        log.debug("Has profileRow:", !!parentAny.profileRow);
        log.debug("Has sourceInvoiceProcessingCache:", !!parentAny.sourceInvoiceProcessingCache);

        // Walk up parent chain
        let currentLayout = this.parentLayout as any;
        let depth = 0;
        while (currentLayout && currentLayout.parent && depth < 10) {
            currentLayout = currentLayout.parent;
            depth++;
            log.debug(`Parent ${depth}:`, currentLayout.constructor?.name || 'unknown');
            log.debug(`  - Has mainDataSource:`, !!currentLayout.mainDataSource);
            log.debug(`  - Has searchMainDatasource:`, !!currentLayout.searchMainDatasource);
            log.debug(`  - Has tabsetInvoiceProcessingTabs:`, !!currentLayout.tabsetInvoiceProcessingTabs);
            log.debug(`  - Has profileRow:`, !!currentLayout.profileRow);
            log.debug(`  - Children count:`, currentLayout.children?.length || 0);

            if (currentLayout.children) {
                currentLayout.children.forEach((child: any, index: number) => {
                    log.debug(`    Child ${index}:`, child.constructor?.name || 'unknown');
                    log.debug(`      - Has tableProcessing:`, !!child.tableProcessing);
                    log.debug(`      - Has filterPanel:`, !!child.filterPanel);
                });
            }
        }
        log.debug("=== END DEBUG ===");
    }
}
